import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import DailyBreadAdmin from './DailyBreadAdmin';
import { useUser } from '../context/UserContext';

// A speed bump, not a lock: this string ships in the bundle, and the check-in
// figures behind it are readable with the public key anyway. Editing the
// devotional is gated properly, on the server, by the save-daily-bread
// function reading the caller's Google session.
const ADMIN_PIN = '12061';

export default function Admin() {
  // An admin account goes straight in; the PIN stays for everyone else. The
  // client-side isAdmin flag only decides what is shown: the check-in figures
  // are readable with the public key anyway, and devotional edits are
  // authorised server-side by the save-daily-bread function.
  const { user } = useUser();
  const [pin,          setPin]          = useState('');
  const [pinAuthed,    setPinAuthed]    = useState(() => sessionStorage.getItem('adminAuthed') === 'true');
  const authed = !!user?.isAdmin || pinAuthed;
  const [pinError,     setPinError]     = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [data,         setData]         = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-CA')); // local date
  const [mainTab,      setMainTab]      = useState('checkins'); // 'checkins' | 'reading'

  useEffect(() => { if (authed) loadAdminData(); }, [authed, selectedDate]);

  function handlePin() {
    if (pin === ADMIN_PIN) {
      sessionStorage.setItem('adminAuthed', 'true');
      setPinAuthed(true);
    } else {
      setPinError(true);
      setPin('');
    }
  }

  async function loadAdminData() {
    setLoading(true);
    // The database does the grouping (by account, so a renamed reader is one
    // person) and returns just what this page shows, instead of this page
    // downloading every check-in each time the date changes.
    const { data: summary, error } = await supabase.rpc('admin_summary', { p_date: selectedDate });
    if (error || !summary) { setLoading(false); return; }
    setData(summary);
    setLoading(false);
  }

  // ── PIN gate ─────────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="page">
        <div className="welcome-card">
          <div className="welcome-emoji">🔐</div>
          <h1>Admin Access</h1>
          <p className="welcome-sub">Enter PIN to continue</p>
          <div className="name-input-row">
            <input className="name-input" type="password" placeholder="Enter PIN…"
              value={pin}
              onChange={e => { setPin(e.target.value); setPinError(false); }}
              onKeyDown={e => e.key === 'Enter' && handlePin()} />
            <button className="start-btn" onClick={handlePin}>Enter →</button>
          </div>
          {pinError && <p style={{color:'#f87171',marginTop:'12px'}}>Incorrect PIN</p>}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-main-tabs">
          <button className={`admin-main-tab${mainTab==='checkins'?' active':''}`} onClick={() => setMainTab('checkins')}>
            📊 Check-ins
          </button>
          <button className={`admin-main-tab${mainTab==='reading'?' active':''}`} onClick={() => setMainTab('reading')}>
            📖 Today's Reading
          </button>
        </div>
      </div>

      {/* ── Check-ins tab ── */}
      {mainTab === 'checkins' && (
        <>
          {(loading || !data) ? (
            <div className="loading">Loading admin data…</div>
          ) : (
            <>
              <div className="admin-date-picker" style={{marginBottom:16}}>
                <input type="date" value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)} className="date-input" />
              </div>
              <div className="stats-row" style={{marginBottom:'24px'}}>
                <div className="stat-card"><div className="stat-number">{data.uniqueUsers}</div><div className="stat-label">Total Users</div></div>
                <div className="stat-card"><div className="stat-number">{data.totalCheckins}</div><div className="stat-label">Total Check-ins</div></div>
                <div className="stat-card"><div className="stat-number">{data.todayReaders.length}</div><div className="stat-label">Read on {selectedDate}</div></div>
              </div>
              <div className="admin-grid">
                <div className="admin-card">
                  <div className="admin-card-title">📅 Readers on {selectedDate} ({data.todayReaders.length})</div>
                  {data.todayReaders.length === 0 ? <div className="lb-empty">No check-ins on this date</div> : (
                    <div className="readers-table">
                      <div className="readers-header"><span>Name</span><span>NT</span><span>OT</span></div>
                      {data.todayReaders.map((r, i) => (
                        <div className="readers-row" key={`${r.name}-${i}`}>
                          <span>{r.name}</span>
                          <span>{r.nt ? <span className="check-nt">✓</span> : '—'}</span>
                          <span>{r.ot ? <span className="check-ot">✓</span> : '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="admin-card">
                  <div className="admin-card-title">⭐ Top Readers (All Time)</div>
                  {data.topReaders.map((r, i) => (
                    <div className="lb-row" key={`${r.name}-${i}`}>
                      <span className="lb-rank">{i+1}</span>
                      <span className="lb-name">{r.name}</span>
                      <span className="lb-score">{r.fullDays} full · {r.totalDays} days</span>
                    </div>
                  ))}
                </div>
                <div className="admin-card admin-card-full">
                  <div className="admin-card-title">⚠️ Haven't Read in 3+ Days ({data.inactive.length})</div>
                  {data.inactive.length === 0 ? <div className="lb-empty">Everyone is on track! 🎉</div> : (
                    <div className="readers-table">
                      <div className="readers-header" style={{gridTemplateColumns:'1fr 140px 100px'}}>
                        <span>Name</span><span>Last Check-in</span><span>Days Ago</span>
                      </div>
                      {data.inactive.map((r, i) => (
                        <div className="readers-row" key={`${r.name}-${i}`} style={{gridTemplateColumns:'1fr 140px 100px'}}>
                          <span>{r.name}</span>
                          <span>{r.lastDate}</span>
                          <span style={{color: r.daysAgo >= 7 ? '#f87171' : '#fbbf24'}}>{r.daysAgo} days</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Today's Reading tab ── */}
      {mainTab === 'reading' && <DailyBreadAdmin />}
    </div>
  );
}
